import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { backend } from '../data';
import { BhStore, type SyncStatus } from '@bhorosha/data';
import type { Membership, Session } from '@bhorosha/data';
import { legacyToast, mountLegacyApp, requestLegacyReload } from '../legacy/mount';
import { t } from '@bhorosha/i18n';
import { ErrorText, Spinner, AuthCard } from '../ui/components';
import { AccountMenu } from './AccountMenu';
import { FamilyPanel } from './FamilyPanel';
import { AccountPanel } from './AccountPanel';
import { Dashboard } from '../monitor/Dashboard';
import { DailyLog } from '../monitor/DailyLog';
import { BpPage } from '../monitor/BpPage';
import { startPhoneWatch } from '../monitor/phoneWatch';

const FRESH_MS = 60_000;

/**
 * Who is signed in, and the family's members, for the original screens
 * (notification bell, "who gets notifications" in the person dialog).
 */
async function shareMembersWithScreens(session: Session, membership: Membership) {
  window.__bhMe = { userId: session.userId, displayName: session.displayName, role: membership.role };
  try {
    window.__bhMembers = (await backend.listMembers(membership.familyId)).map(m => ({
      userId: m.userId, displayName: m.displayName || m.email, email: m.email, role: m.role, allPatients: m.allPatients, patientIds: m.patientIds,
    }));
  } catch (e) { console.warn('[Bhorosha] could not load members', e); }
  window.__bhMembersChanged?.();
}

function applyRoleClasses(m: Membership) {
  const b = document.body.classList;
  b.remove('bh-role-owner', 'bh-role-editor', 'bh-role-viewer', 'bh-can-manage-people');
  b.add(`bh-role-${m.role}`);
  if (m.role === 'owner' || (m.role === 'editor' && m.allPatients)) b.add('bh-can-manage-people');
}

/**
 * Loads one family's data, then shows the original app screens with the
 * account / family menu in the header.
 */
export function FamilyApp({ session, membership, memberships, onSwitchFamily, onNewFamily, onMembershipsChanged }: {
  session: Session;
  membership: Membership;
  memberships: Membership[];
  onSwitchFamily: (id: string) => void;
  onNewFamily: () => void;
  onMembershipsChanged: () => Promise<Membership[]>;
}) {
  const storeRef = useRef<BhStore | null>(null);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading');
  const [err, setErr] = useState('');
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [sync, setSync] = useState<{ status: SyncStatus; pending: number }>({ status: 'saved', pending: 0 });
  const [panel, setPanel] = useState<null | 'family' | 'account'>(null);
  const [role, setRole] = useState(membership.role);
  const [roots, setRoots] = useState<{ log: HTMLElement; bp: HTMLElement; home: HTMLElement | null } | null>(null);
  const [page, setPage] = useState('home');

  // Daily log + BP tabs: the original screens make empty pages; draw into them.
  useEffect(() => {
    const find = () => {
      const log = document.getElementById('bhDailyLogRoot'), bp = document.getElementById('bhBpRoot'), home = document.getElementById('bhHomeRoot');
      if (log && bp) setRoots(r => (r && r.log === log && r.home === home ? r : { log, bp, home }));
    };
    const onPage = (e: Event) => setPage(String((e as CustomEvent).detail || ''));
    window.addEventListener('bh:tabs-ready', find);
    window.addEventListener('bh:page', onPage);
    const iv = setInterval(find, 500);
    find();
    return () => { window.removeEventListener('bh:tabs-ready', find); window.removeEventListener('bh:page', onPage); clearInterval(iv); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const store = new BhStore({
      familyId: membership.familyId, userId: session.userId, role: membership.role, backend,
      persist: localStorage,
      onExternalChange: () => { requestLegacyReload(); window.dispatchEvent(new Event('bh:data')); },
      onStatus: (status, pending) => setSync({ status, pending }),
      onNotice: msg => legacyToast(msg),
    });
    storeRef.current = store;
    applyRoleClasses(membership);
    void shareMembersWithScreens(session, membership);
    const stopPhones = startPhoneWatch(membership.familyId);

    (async () => {
      const hadCache = store.loadCache();
      if (hadCache) {
        start();
        // Bring the offline copy up to date in the background.
        store.refresh().then(changed => { if (changed) requestLegacyReload(); }).catch(() => setSync(s => ({ ...s, status: 'offline' })));
      } else {
        try {
          await store.refresh();
          if (!cancelled) start();
        } catch (e) {
          console.error(e);
          if (!cancelled) { setErr(t('err.loadFamily')); setPhase('error'); }
        }
      }
    })();

    function start() {
      const host = document.getElementById('bh-legacy');
      if (cancelled || !host) return;
      mountLegacyApp(store, host);
      loadCatalog();
      // "Coming up" column: always open on wide screens, folded on phones.
      const wide = window.matchMedia('(min-width: 1000px)');
      const syncComingUp = () => { const d = document.getElementById('bhComingUp') as HTMLDetailsElement | null; if (d) d.open = wide.matches; };
      syncComingUp();
      wide.addEventListener('change', syncComingUp);
      setSlot(document.getElementById('bhAccountSlot'));
      setPhase('ready');
    }

    // The shared Medicine Library (kept by the admin): "+ Add Medicine"
    // suggests from it (legacy/overrides/catalog.js). Refreshed when the tab
    // comes back into view.
    function loadCatalog() {
      backend.listCatalog().then(list => {
        if (cancelled) return;
        const w = window as unknown as { __bhCatalog?: unknown; __bhCatalogLoaded?: () => void };
        w.__bhCatalog = list; w.__bhCatalogLoaded?.();
      }).catch(e => console.warn('medicine list', e));
    }
    const onVisible = () => { if (document.visibilityState === 'visible') loadCatalog(); };
    document.addEventListener('visibilitychange', onVisible);

    // Live changes from other devices / family members.
    const unsub = backend.subscribe(membership.familyId, async ev => {
      if (ev.kind === 'membership') {
        const list = await onMembershipsChanged();
        const me = list.find(m => m.familyId === membership.familyId);
        if (!me) { location.reload(); return; } // removed from this family
        store.role = me.role; setRole(me.role); applyRoleClasses(me);
        void shareMembersWithScreens(session, me);
        if (await store.refresh().catch(() => false)) requestLegacyReload();
        return;
      }
      if (store.applyRemote(ev)) requestLegacyReload();
    });

    // Catch up after being offline or in the background.
    const catchUp = () => {
      if (document.hidden) return;
      if (Date.now() - store.lastFetchedAt < FRESH_MS) return;
      store.refresh().then(changed => { if (changed) requestLegacyReload(); }).catch(() => {});
      void store.flush();
    };
    const onOnline = () => { void store.flush(); catchUp(); };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', catchUp);
    const beforeUnload = (e: BeforeUnloadEvent) => { store.saveNow(); if (store.pending && navigator.onLine) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', beforeUnload);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      unsub();
      stopPhones();
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', catchUp);
      window.removeEventListener('beforeunload', beforeUnload);
    };
    // The legacy app can only be mounted once per page; switching family reloads the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function signOut() {
    const store = storeRef.current;
    if (store?.pending) { try { await store.flush(); } catch { /* ignore */ } }
    if (store?.pending && !confirm('Some changes have not reached the server yet and will be lost if you sign out now. Sign out anyway?')) return;
    BhStore.clearDeviceCache(localStorage);
    await backend.signOut();
    location.href = '/';
  }

  const current: Membership = { ...membership, role };
  return (
    <>
      {phase === 'loading' && <Spinner />}
      {phase === 'error' && (
        <AuthCard>
          <ErrorText>{err}</ErrorText>
          <button className="bh-btn bh-btn-primary bh-full" onClick={() => location.reload()}>{t('common.tryAgain')}</button>
        </AuthCard>
      )}
      {slot && createPortal(
        <AccountMenu
          session={session} membership={current} memberships={memberships} sync={sync}
          onOpenFamily={() => setPanel('family')} onOpenAccount={() => setPanel('account')}
          onSwitchFamily={onSwitchFamily} onNewFamily={onNewFamily} onSignOut={signOut}
        />, slot)}
      {panel === 'family' && storeRef.current && (
        <FamilyPanel session={session} membership={current} store={storeRef.current} onClose={() => setPanel(null)} onChanged={onMembershipsChanged} />
      )}
      {panel === 'account' && <AccountPanel session={session} onClose={() => setPanel(null)} />}
      {roots?.home && storeRef.current && createPortal(<Dashboard store={storeRef.current} familyId={membership.familyId} active={page === 'home'} />, roots.home)}
      {roots && storeRef.current && createPortal(<DailyLog store={storeRef.current} familyId={membership.familyId} active={page === 'log'} />, roots.log)}
      {roots && storeRef.current && createPortal(<BpPage store={storeRef.current} familyId={membership.familyId} active={page === 'bp'} />, roots.bp)}
    </>
  );
}
